import React from "react";
import { enFAQ } from "./faq.js";
export const Link = ({ children, ...props }) => (
  <a {...props} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const introText = (
  <>
    The China’s Global Power (CGP) Database tracks global power plants outside
    of China financed by Chinese foreign direct investment (FDI) and/or China's
    two development finance institutions (DFIs)—the Export-Import Bank of China
    (CHEXIM) and the China Development Bank (CDB).
    <br />
    <br />
    From 2000-2023, Chinese DFIs and investors have supported 745 power plants
    overseas in 96 countries, representing 1,542 power units providing a total
    of 177 GW of power plant capacity. Among these power plants, Chinese capital
    participation includes FDI in the form of greenfield and mergers and
    acquisitions (M&A), as well as DFI debt financing.
  </>
);
const aboutText = (
  <div>
    <p>
      The China’s Global Power (CGP) Database tracks global power plants outside
      of China financed by Chinese foreign direct investment (FDI) and/or
      lending from China's two development finance institutions (DFIs)— the
      Export-Import Bank of China (CHEXIM) and the China Development Bank (CDB).
      The database also tracks and displays the deal types, Chinese investors
      and/or lenders, percentage of ownership by investor, amount of capacity in
      megawatts (MW), type of technology, operating status, funding and
      commission year and the estimated annual CO2 emissions of Chinese financed
      overseas power plants.
    </p>
    <p>
      The CGP Database is managed by the Boston University Global Development
      Policy Center (GDP Center), a University-wide center in partnership with
      the Frederick S. Pardee School of Global Studies at Boston University. The
      GDP Center’s mission is to advance policy-oriented research for financial
      stability, human well-being and environmental sustainability.
    </p>
    <p>
      CDB and CHEXIM do not currently publish their lending data at a
      disaggregated level. Over the years, the GDP Center has developed a
      tracking methodology to fill this data gap.
    </p>
    <p style={{ fontWeight: "bold" }}>Contact:</p>
    <p>
      For technical inquiries or suggestions regarding this database, please
      contact Diego Morro, <a href="mailto:dm3760@bu.edu">dm3760@bu.edu</a>.
    </p>
    <p>
      For media inquiries, please contact Sam Igo,{" "}
      <a href="mailto:sigo@bu.edu">sigo@bu.edu</a>.
    </p>
    <p>Database last updated: May, 2025.</p>
    <p style={{ fontWeight: "bold" }}>Suggested Citation:</p>
    <p>
      Boston University Global Development Policy Center. 2025. China’s Global
      Power Database. Retrieved from{" "}
      <a href="https://www.bu.edu/cgp/">https://www.bu.edu/cgp/</a>
    </p>
    <p style={{ fontWeight: "bold" }}>Resources:</p>
    <p>
      For information on data methodology for the 2025 CGP Database update,
      please refer to the{" "}
      <Link
        style={{ fontWeight: "bold" }}
        href="https://www.bu.edu/gdp/2021/03/23/gdp-center-database-methodology-guidebook/"
      >
        GDP Center Database Methodology Guidebook
      </Link>{" "}
      and the appendix of the{" "}
      <Link href="https://www.bu.edu/gdp/2025/04/28/no-new-coal-a-shift-in-the-composition-of-chinas-overseas-power-plant-portfolio/">
        2025 Policy Brief
      </Link>
      . For information on data and methodology for the first iteration of the
      CGP Database (2020), please refer to the{" "}
      <Link href="https://www.bu.edu/gdp/2020/10/19/chinas-global-power-database-methodology-note/">
        2020 Methodology Note
      </Link>
      .
    </p>
    <p>
      For research and policy commentary, please refer to the publications
      below:
    </p>
    <ul>
      <li>
        <Link href="https://www.bu.edu/gdp/2025/04/28/no-new-coal-a-shift-in-the-composition-of-chinas-overseas-power-plant-portfolio/">
          2025 Policy Brief
        </Link>
      </li>
      <li>
        <Link href="https://www.bu.edu/gdp/2022/10/19/understanding-chinas-global-power-2022-update/">
          2022 Policy Brief
        </Link>
      </li>
      <li>
        <Link href="https://www.bu.edu/gdp/2020/10/19/chinas-global-power-database-policy-brief/">
          2020 Policy Brief
        </Link>
      </li>
      <li>
        <Link href="https://www.bu.edu/gdp/2020/10/23/chinas-global-power-estimating-chinese-foreign-direct-investment-in-the-electric-power-sector-2/">
          2020 Journal Article
        </Link>{" "}
        in Energy Policy
      </li>
      <li>
        <Link href="https://www.bu.edu/gdp/2020/10/08/chinese-overseas-development-financing-of-electric-power-generation-a-comparative-analysis/">
          2020 Journal Article
        </Link>{" "}
        in One Earth
      </li>
      <li>
        <Link href="https://www.bu.edu/gdp/2018/10/02/energizing-development-finance-the-benefits-risks-of-chinas-development-finance-in-the-global-energy-sector/">
          2018 Journal Article
        </Link>{" "}
        in Energy Policy
      </li>
    </ul>
    <p style={{ fontWeight: "bold" }}>Acknowledgements:</p>
    <p>
      The Boston University Global Development Policy Center would like to thank
      Zhongshu Li, Xu Chen and Xinyue Ma for foundational research for this
      database. The Center also thanks Professor Denise L. Mauzerall at the
      Department Civil and Environmental Engineering and the School of Public
      and International Affairs, Princeton University for her participation in
      some of the academic papers that led to this project and gratefully
      acknowledges Thang Ha, Shengheng Li, Jingjing Li and Victoria Bien Aime
      for research assistance. Finally, the GDP Center would like to thank the
      Charles Stewart Mott Foundation, the European Climate Foundation, the
      Hewlett Foundation and the Rockefeller Brothers Fund for the financial
      support that enabled this research and interactive database.
    </p>
    <p>
      The China’s Global Power Database was created by{" "}
      <Link href="https://pitchinteractive.com/">Pitch Interactive</Link>.
    </p>
    <p style={{ fontWeight: "bold" }}>Data Download:</p>
    <p>To download the data, please fill out the form submission below:</p>
  </div>
);
const multiAttributeCopy = (
  <span>
    Select one or more countries and regions of interest to see power generation
    capacity and estimated annual CO<sub>2</sub> emission with Chinese capital
    by 2030. You can generate a graph to demonstrate the status of generation
    capacity in the selected countries or regions as of the end of 2018 across
    technology types, and estimated annual CO<sub>2</sub> emission from these
    power plants by 2030.
  </span>
);

export const enObj = {
  // general
  "China’s Global Power Database": "China’s Global Power Database",
  "China’s Foreign Direct Investment and Development Finance in Power Generation Overseas":
    "China’s Foreign Direct Investment and Development Finance in Power Generation Overseas",
  "About / Data Download": "About / Data Download",
  Overview: "Overview",
  "Multi-Attribute Comparison": "Multi-Attribute Comparison",
  multiAttributeCopy,
  "Choose a Modifier": "Choose a Modifier",
  "Choose up to 9": "Choose up to 9",
  "Generate Report": "Generate Report",
  "Modify Report": "Modify Report",
  "Export PDF": "Export PDF",
  "POWER GENERATION CAPACITY AND ESTIMATED ANNUAL CO2 EMISSION WITH CHINESE CAPITAL BY 2033":
    "POWER GENERATION CAPACITY AND ESTIMATED ANNUAL CO₂ EMISSIONS WITH CHINESE CAPITAL BY 2033",
  "Capacity (MW) by Status 2000-2030": "Capacity (MW) by Status 2000-2030",
  "Capacity (MW) by Technology": "Capacity (MW) by Technology",
  "CO2 Emissions (K Ton) by Technology": "CO2 Emissions (K Ton) by Technology",
  "Estimated Annual Emission from Power Generation with Chinese Capital":
    "Estimated Annual Emissions from Power Generation with Chinese Capital",
  GLOBAL: "Global",
  Year: "Year",
  "Expand All": "Expand All",
  "Collapse All": "Collapse All",
  "Change Timeframe": "Change Timeframe",
  Timeframe: "Timeframe",
  "Select All": "Select All",
  "Deselect All": "Deselect All",
  Cancel: "Cancel",
  Apply: "Apply",
  Close: "Close",
  "Plant Name": "Plant Name",
  Capacity: "Capacity",
  "Capacity (MW)": "Capacity (MW)",
  Status: "Status",
  "Project Status": "Project Status",
  "Year of Commission": "Year of Commission",
  Technology: "Technology",
  State: "State",
  Countries: "Countries",
  Country: "Country",
  Regions: "Regions",
  Region: "Region",
  "CO2 Emissions": "CO2 Emissions",
  Emissions: "Emissions",
  "Estimated Annual CO2 Emissions from Power Generation (1000 ton)":
    "Estimated Annual CO2 Emissions from Power Generation (1000 ton)",
  "Deal Type": "Deal Type",
  "FDI?": "FDI?",
  FDI: "FDI",
  DFI: "DFI",
  "Investing Company": "Investing Company",
  "Investing Country Ownership %": "Investing Country Ownership %",
  "FDI Deal Type": "FDI Deal Type",
  "Involving Chinese DFI?": "Involving Chinese DFI?",
  "Involving Chinese Policy Bank?": "Involving Chinese Policy Bank?",
  Lender: "Lender",
  Borrower: "Borrower",
  Source: "Source",
  "In Operation": "In Operation",
  "Under Construction": "Under Construction",
  "Under Planning": "Under Planning",
  Pending: "Pending",
  Wind: "Wind",
  Coal: "Coal",
  Hydropower: "Hydropower",
  Solar: "Solar",
  Gas: "Gas",
  Biomass: "Biomass",
  Oil: "Oil",
  Nuclear: "Nuclear",
  Geothermal: "Geothermal",
  SPAIN: "SPAIN",
  PORTUGAL: "PORTUGAL",
  ETHIOPIA: "ETHIOPIA",
  INDONESIA: "INDONESIA",
  TOGO: "TOGO",
  "UNITED KINGDOM": "UNITED KINGDOM",
  BRAZIL: "BRAZIL",
  MEXICO: "MEXICO",
  BANGLADESH: "BANGLADESH",
  MALAYSIA: "MALAYSIA",
  INDIA: "INDIA",
  UZBEKISTAN: "UZBEKISTAN",
  JAPAN: "JAPAN",
  ITALY: "ITALY",
  CHILE: "CHILE",
  "REPUBLIC OF KOREA": "REPUBLIC OF KOREA",
  TURKEY: "TURKEY",
  JORDAN: "JORDAN",
  PAKISTAN: "PAKISTAN",
  IRELAND: "IRELAND",
  CANADA: "CANADA",
  GAMBIA: "GAMBIA",
  "UNITED STATES": "UNITED STATES",
  BELARUS: "BELARUS",
  CAMEROON: "CAMEROON",
  KYRGYZSTAN: "KYRGYZSTAN",
  NEPAL: "NEPAL",
  GHANA: "GHANA",
  "CONGO DEM REP": "CONGO DEM REP",
  "CONGO, DEM. REP.": "CONGO, DEM. REP.",
  ARGENTINA: "ARGENTINA",
  AUSTRALIA: "AUSTRALIA",
  VIETNAM: "VIETNAM",
  ZAMBIA: "ZAMBIA",
  GEORGIA: "GEORGIA",
  BULGARIA: "BULGARIA",
  MYANMAR: "MYANMAR",
  ECUADOR: "ECUADOR",
  "SOUTH AFRICA": "SOUTH AFRICA",
  MALTA: "MALTA",
  CHAD: "CHAD",
  "EQUATORIAL GUINEA": "EQUATORIAL GUINEA",
  ROMANIA: "ROMANIA",
  UKRAINE: "UKRAINE",
  TAJIKISTAN: "TAJIKISTAN",
  "PAPUA NEW GUINEA": "PAPUA NEW GUINEA",
  KAZAKHSTAN: "KAZAKHSTAN",
  SUDAN: "SUDAN",
  BELGIUM: "BELGIUM",
  KENYA: "KENYA",
  NIGERIA: "NIGERIA",
  MALI: "MALI",
  GABON: "GABON",
  "COTE D'IVOIRE": "科特迪瓦",
  FRANCE: "FRANCE",
  "SRI LANKA": "SRI LANKA",
  "UNITED ARAB EMIRATES": "UNITED ARAB EMIRATES",
  MALDIVES: "MALDIVES",
  ZIMBABWE: "ZIMBABWE",
  POLAND: "POLAND",
  CONGO: "CONGO",
  UGANDA: "UGANDA",
  MOROCCO: "MOROCCO",
  COLOMBIA: "COLOMBIA",
  CUBA: "CUBA",
  GUINEA: "GUINEA",
  CAMBODIA: "CAMBODIA",
  MALAWI: "MALAWI",
  "SAUDI ARABIA": "SAUDI ARABIA",
  SERBIA: "SERBIA",
  THAILAND: "THAILAND",
  NETHERLANDS: "NETHERLANDS",
  PHILIPPINES: "PHILIPPINES",
  SWEDEN: "SWEDEN",
  GERMANY: "GERMANY",
  MONTENEGRO: "MONTENEGRO",
  FIJI: "FIJI",
  LAOS: "LAOS",
  "SOUTH SUDAN": "SOUTH SUDAN",
  EGYPT: "EGYPT",
  BOLIVIA: "BOLIVIA",
  PERU: "PERU",
  "BOSNIA AND HERZEGOVINA": "BOSNIA AND HERZEGOVINA",
  "NEW ZEALAND": "NEW ZEALAND",
  SINGAPORE: "SINGAPORE",
  HUNGARY: "HUNGARY",
  RUSSIA: "RUSSIA",
  "EUROPE/CENTRAL ASIA": "EUROPE/CENTRAL ASIA",
  AFRICA: "AFRICA",
  "SOUTHEAST ASIA": "SOUTHEAST ASIA",
  LAC: "LAC",
  "SOUTH ASIA": "SOUTH ASIA",
  "EAST ASIA": "EAST ASIA",
  "MIDDLE EAST": "MIDDLE EAST",
  "NORTH AMERICA": "NORTH AMERICA",
  OCEANIA: "OCEANIA",
  Greenfield: "Greenfield",
  "M&A": "M&A",
  "DFI only": "DFI only",
  "FDI + DFI": "FDI + DFI",
  "Policy bank only": "Policy bank only",
  "FDI + Policy bank": "FDI + Policy bank",
  Yes: "Yes",
  No: "No",
  NA: "NA",
  "View in Table": "View in Table",
  "Ex-Im Bank": "Ex-Im Bank",
  CDB: "CDB",
  "CDB-Ex-Im Cofinancing": "CDB-Ex-Im Cofinancing",
  // companies
  "Beijing Jingneng & PowerChina": "Beijing Jingneng & PowerChina",
  "Canadian Solar": "Canadian Solar",
  CETIC: "CETIC",
  ChemChina: "ChemChina",
  "CHINA COMMUNICATION CONSTRUCTION CO LTD":
    "CHINA COMMUNICATION CONSTRUCTION CO LTD",
  "China Datang": "China Datang",
  "China General Nuclear Power Group": "China General Nuclear Power Group",
  "China General Technology": "China General Technology",
  "China Gezhouba": "China Gezhouba",
  "China Huadian Corporation": "China Huadian Corporation",
  "China Huaneng Group": "China Huaneng Group",
  "China International Trust & Investment Corp - CITIC":
    "China International Trust & Investment Corp - CITIC",
  "China Investment Corp": "China Investment Corp",
  "China Metallurgical Group Corporation":
    "China Metallurgical Group Corporation",
  "China National Building Material": "China National Building Material",
  "China National Electrical Engineering Corporation":
    "China National Electrical Engineering Corporation",
  "China National Machinery Industry Corporation":
    "China National Machinery Industry Corporation",
  "China National Offshore Oil Corporation":
    "China National Offshore Oil Corporation",
  "China National Petroleum Corporation":
    "China National Petroleum Corporation",
  "CHINA POWER INVESTMENT CORP": "CHINA POWER INVESTMENT CORP",
  "China Southern Power Grid": "China Southern Power Grid",
  "CHINA SUNERGY CO": "CHINA SUNERGY CO",
  "China Three Gorges Corporation": "China Three Gorges Corporation",
  Chinaerdos: "Chinaerdos",
  Chint: "Chint",
  "CLENERGY (XIAMEN) TECH CO LTD": "CLENERGY (XIAMEN) TECH CO LTD",
  "CLP Holdings": "CLP Holdings",
  "Datang International Power Generation":
    "Datang International Power Generation",
  "Envision Energy": "Envision Energy",
  "ET SOLAR": "ET SOLAR",
  "Fuxing Xiaocheng": "Fuxing Xiaocheng",
  "Guodian Group": "Guodian Group",
  "HANERGY GROUP CO LTD": "HANERGY GROUP CO LTD",
  "Harbin Electric": "Harbin Electric",
  "Hareon Solar": "Hareon Solar",
  "JIANGSU ZHENFA HOLDINGS": "JIANGSU ZHENFA HOLDINGS",
  "Jinko Solar": "Jinko Solar",
  "JINKOSOLAR HOLDING CO LTD": "JINKOSOLAR HOLDING CO LTD",
  "LDK Solar": "LDK Solar",
  "Metallurgical Corporation of China": "Metallurgical Corporation of China",
  "Panda Green Energy Group": "Panda Green Energy Group",
  PowerChina: "PowerChina",
  ReneSola: "ReneSola",
  "RISEN ENERGY CO LTD": "RISEN ENERGY CO LTD",
  "SHANGHAI ELECTRIC GROUP": "SHANGHAI ELECTRIC GROUP",
  "Shenhua Group": "Shenhua Group",
  "Shenzhen Energy Corporation": "Shenzhen Energy Corporation",
  "Shenzhen Nanshan Power Co": "Shenzhen Nanshan Power Co",
  "Shunfeng Investment Limited": "Shunfeng Investment Limited",
  Sinopec: "Sinopec",
  "State Development and Investment": "State Development and Investment",
  "State Grid Corporation of China": "State Grid Corporation of China",
  "State Power Investment Corporation": "State Power Investment Corporation",
  Suntech: "Suntech",
  "Trina Solar": "Trina Solar",
  "Union Resources & Engineering Corporation":
    "Union Resources & Engineering Corporation",
  "XIANGTAN ELECTRIC MANUFACTURE CORP": "XIANGTAN ELECTRIC MANUFACTURE CORP",
  "Xinjiang Goldwind Science & Technology (Goldwind)":
    "Xinjiang Goldwind Science & Technology (Goldwind)",
  "Zhefu Holdings": "Zhefu Holdings",
  "ZHEJIANG KAISHAN COMPRESSOR CO": "ZHEJIANG KAISHAN COMPRESSOR CO",
  "Zhenfa New Energy": "Zhenfa New Energy",
  ZTE: "ZTE",
  //
  ALL: "ALL",
  Explore: "Explore",
  FAQ: "Frequently Asked Questions (FAQ)",
  Language: "Language",
  "Ownership %": "Ownership %",
  "Click to Lock Project": "Click to Lock Project",
  "Click to Unlock Project": "Click to Unlock Project",
  "K Ton": "K Ton",
  Government: "Government",
  Share: "Share",
  Project: "Project",
  Total: "Total",
  "Cumulative Total": "Cumulative Total",
  // new
  "by Status": "by Status",
  "by Technology": "by Technology",

  "State/Province": "State/Province",
  Waste: "Waste",
  Annual: "Annual",
  "Coal, Gas": "Coal, Gas",
  "Gas, Oil": "Gas, Oil",
  "In Operation, Under Construction": "In Operation, Under Construction",
  "Under Construction, Under Planning": "Under Construction, Under Planning",
  "In Operation, Under Planning": "In Operation, Under Planning",
  "Multiple values": "This plant has multiple units with multiple values.",
  Plants: "Plants",
  // jsx
  introText,
  aboutText,
  faqText: enFAQ,
};
